import styled from "styled-components";
import { pxIntoRem } from "../../utils/pxIntoRem";

// Styled Components with Image prefix
const ImageBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
  padding: ${pxIntoRem(20)} 0;
`;

const ImageCard = styled.div`
  background: white;
  padding: ${pxIntoRem(40)};
  border-radius: ${pxIntoRem(12)};
  box-shadow: 0 ${pxIntoRem(2)} ${pxIntoRem(12)} rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: ${pxIntoRem(1200)};
  border: ${pxIntoRem(1)} solid #e9ecef;
`;

const ImageTitle = styled.h2`
  text-align: center;
  margin-bottom: ${pxIntoRem(32)};
  color: #212529;
  font-size: ${pxIntoRem(28)};
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const ImageFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxIntoRem(6)};
  margin-bottom: ${pxIntoRem(16)};
`;

const ImageInput = styled.input<{ $hasError?: boolean }>`
  padding: ${pxIntoRem(14)} ${pxIntoRem(16)};
  border: ${pxIntoRem(1)} solid ${({ $hasError }) => ($hasError ? "#e74c3c" : "#dee2e6")};
  border-radius: ${pxIntoRem(8)};
  font-size: ${pxIntoRem(15)};
  transition: all 0.2s ease;
  background: #fff;
  color: #212529;

  &:focus {
    outline: none;
    border-color: ${({ $hasError }) => ($hasError ? "#e74c3c" : "#495057")};
    box-shadow: 0 0 0 ${pxIntoRem(3)}
      ${({ $hasError }) => ($hasError ? "rgba(231, 76, 60, 0.1)" : "rgba(73, 80, 87, 0.1)")};
  }

  &::placeholder {
    color: #6c757d;
    font-weight: 400;
  }

  &:hover:not(:focus) {
    border-color: ${({ $hasError }) => ($hasError ? "#e74c3c" : "#adb5bd")};
  }
`;

const ImageTextArea = styled.textarea<{ $hasError?: boolean }>`
  padding: ${pxIntoRem(14)} ${pxIntoRem(16)};
  border: ${pxIntoRem(1)} solid ${({ $hasError }) => ($hasError ? "#e74c3c" : "#dee2e6")};
  border-radius: ${pxIntoRem(8)};
  font-size: ${pxIntoRem(15)};
  transition: all 0.2s ease;
  background: #fff;
  color: #212529;
  resize: vertical;
  min-height: ${pxIntoRem(120)};
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;

  &:focus {
    outline: none;
    border-color: ${({ $hasError }) => ($hasError ? "#e74c3c" : "#495057")};
    box-shadow: 0 0 0 ${pxIntoRem(3)}
      ${({ $hasError }) => ($hasError ? "rgba(231, 76, 60, 0.1)" : "rgba(73, 80, 87, 0.1)")};
  }
`;

const ImageButton = styled.button<{ $disabled?: boolean; $variant?: "primary" | "secondary" }>`
  padding: ${pxIntoRem(14)} ${pxIntoRem(24)};
  background: ${({ $disabled, $variant }) => {
    if ($disabled) return "#e9ecef";
    return $variant === "primary" ? "#212529" : "#6c757d";
  }};
  color: ${({ $disabled }) => ($disabled ? "#6c757d" : "white")};
  border: none;
  border-radius: ${pxIntoRem(8)};
  font-size: ${pxIntoRem(15)};
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  flex: 1;

  &:hover {
    background: ${({ $disabled, $variant }) => {
      if ($disabled) return "#e9ecef";
      return $variant === "primary" ? "#343a40" : "#5a6268";
    }};
    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-1px)")};
  }

  &:active {
    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(0)")};
  }
`;

const ImageErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  margin-top: ${pxIntoRem(16)};
  padding: ${pxIntoRem(12)} ${pxIntoRem(16)};
  background: #fdf2f2;
  border-radius: ${pxIntoRem(6)};
  border: ${pxIntoRem(1)} solid #fbb;
  font-size: ${pxIntoRem(14)};
  line-height: 1.4;
`;

const ImageComparison = styled.div`
  display: flex;
  gap: ${pxIntoRem(40)};
  margin-bottom: ${pxIntoRem(32)};
  align-items: stretch;
  min-height: ${pxIntoRem(400)};
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ImageWrapper = styled.div`
  max-width: 100%;
  max-height: ${pxIntoRem(350)};
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${pxIntoRem(1)} solid #e9ecef;
  border-radius: ${pxIntoRem(8)};
  padding: ${pxIntoRem(20)};
  background: #f8f9fa;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ImageLatexError = styled.div`
  color: #e74c3c;
  text-align: center;
  padding: ${pxIntoRem(20)};
  background: #fdf2f2;
  border-radius: ${pxIntoRem(6)};
  border: ${pxIntoRem(1)} solid #fbb;
  font-size: ${pxIntoRem(14)};
`;

const ImageControls = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${pxIntoRem(20)};
`;

const ImageActions = styled.div`
  display: flex;
  gap: ${pxIntoRem(12)};
  margin-top: ${pxIntoRem(24)};
`;

const ImageInstructions = styled.div`
  margin-top: ${pxIntoRem(40)};
  padding: ${pxIntoRem(24)};
  background: #f8f9fa;
  border-radius: ${pxIntoRem(8)};
  border: ${pxIntoRem(1)} solid #e9ecef;
`;

const ImageInstructionsTitle = styled.h3`
  margin-bottom: ${pxIntoRem(16)};
  color: #212529;
  font-size: ${pxIntoRem(18)};
  font-weight: 600;
`;

const ImageInstructionsText = styled.p`
  color: #495057;
  font-size: ${pxIntoRem(14)};
  line-height: 1.5;
  margin-bottom: ${pxIntoRem(12)};
`;

const ImageRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxIntoRem(12)};
  margin: ${pxIntoRem(16)} 0;
`;

const ImageRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${pxIntoRem(12)};
  cursor: pointer;
  font-size: ${pxIntoRem(14)};
  color: #495057;
  padding: ${pxIntoRem(8)} ${pxIntoRem(12)};
  border-radius: ${pxIntoRem(6)};
  transition: all 0.2s ease;
  border: ${pxIntoRem(1)} solid transparent;

  &:hover {
    background: #f8f9fa;
    border-color: #e9ecef;
  }

  input[type="radio"] {
    display: none;
  }
`;

const ImageRadio = styled.div<{ $checked: boolean }>`
  width: ${pxIntoRem(18)};
  height: ${pxIntoRem(18)};
  border: ${pxIntoRem(2)} solid ${({ $checked }) => ($checked ? "#212529" : "#adb5bd")};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: ${({ $checked }) => ($checked ? "#212529" : "transparent")};

  &::after {
    content: "";
    width: ${pxIntoRem(8)};
    height: ${pxIntoRem(8)};
    border-radius: 50%;
    background: white;
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
`;

const ImageRadioText = styled.span<{ $checked: boolean }>`
  font-weight: ${({ $checked }) => ($checked ? "600" : "400")};
  color: ${({ $checked }) => ($checked ? "#212529" : "#495057")};
  transition: all 0.2s ease;
`;

export {
  ImageActions,
  ImageBlock,
  ImageButton,
  ImageCard,
  ImageComparison,
  ImageControls,
  ImageDisplay,
  ImageErrorMessage,
  ImageFormGroup,
  ImageInput,
  ImageInstructions,
  ImageInstructionsText,
  ImageInstructionsTitle,
  ImageLatexError,
  ImagePreview,
  ImageRadio,
  ImageRadioGroup,
  ImageRadioLabel,
  ImageRadioText,
  ImageSection,
  ImageTitle,
  ImageWrapper,
  ImageTextArea,
};
